import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useT } from "@/i18n/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, CreditCard, FileDown } from "lucide-react";
import { format, parseISO } from "date-fns";
import { ptBR, enUS, es } from "date-fns/locale";

const METHODS = ["dinheiro", "pix", "zelle", "cartao", "transferencia"];
const STATUSES = ["pago", "pendente"];

const empty = { rental_id: "", amount: 0, method: "pix", status: "pago", notes: "" };

const DATE_LOCALES = { pt: ptBR, en: enUS, es };

export default function Pagamentos() {
  const { t, lang, fmtCurrency } = useT();
  const dateLocale = DATE_LOCALES[lang] || enUS;
  const [items, setItems] = useState([]);
  const [rentals, setRentals] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const [p, r] = await Promise.all([api.get("/payments"), api.get("/rentals")]);
    setItems(p.data); setRentals(r.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payments", { ...form, amount: Number(form.amount) });
      toast.success(t("payments.created"));
      setOpen(false); setForm(empty); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("payments.confirmDelete"))) return;
    try { await api.delete(`/payments/${id}`); toast.success(t("payments.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadReceipt = async (paymentId) => {
    try {
      const r = await api.get(`/payments/${paymentId}/receipt.pdf?lang=${lang}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const totals = items.reduce((acc, p) => {
    if (p.status === "pago") acc.paid += Number(p.amount);
    else acc.pending += Number(p.amount);
    return acc;
  }, { paid: 0, pending: 0 });

  return (
    <div className="space-y-6" data-testid="pagamentos-page">
      <div className="flex items-center justify-between">
        <div>
          <div className="overline">{t("payments.pageTitle", { n: items.length })}</div>
          <p className="text-sm text-zinc-500 mt-1">{t("payments.pageSubtitle")}</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setForm(empty); setOpen(true); }} className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="add-payment-button">
              <Plus className="w-4 h-4 mr-2" /> {t("payments.newPayment")}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg rounded-none">
            <DialogHeader><DialogTitle className="font-display tracking-tight">{t("payments.newPayment")}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <Field label={t("payments.contract")}>
                <Select value={form.rental_id} onValueChange={(v) => setForm({ ...form, rental_id: v })}>
                  <SelectTrigger className="rounded-none"><SelectValue placeholder={t("common.select")} /></SelectTrigger>
                  <SelectContent>
                    {rentals.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.car?.model} · {r.customer?.name} · {fmtCurrency(r.total)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label={t("payments.amount")}><Input type="number" step="0.01" className="rounded-none" required value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="payment-amount-input" /></Field>
                <Field label={t("payments.method")}>
                  <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{t(`payments.methods.${m}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
                <Field label={t("common.status")}>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger className="rounded-none"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`payments.status.${s}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              <Field label={t("common.notes")}><Input className="rounded-none" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} className="rounded-none">{t("common.cancel")}</Button>
                <Button type="submit" className="rounded-none bg-[#F5A623] hover:bg-[#D97706]" data-testid="save-payment-button">{t("common.save")}</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-l border-t border-zinc-200">
        <div className="border-r border-b border-zinc-200 -ml-px -mt-px p-6">
          <div className="overline mb-3">{t("payments.received")}</div>
          <div className="font-display font-black text-4xl tracking-tighter text-green-700">{fmtCurrency(totals.paid)}</div>
        </div>
        <div className="border-r border-b border-zinc-200 -mt-px p-6">
          <div className="overline mb-3">{t("payments.pending")}</div>
          <div className="font-display font-black text-4xl tracking-tighter text-amber-700">{fmtCurrency(totals.pending)}</div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="border border-zinc-200 p-16 text-center">
          <CreditCard className="w-12 h-12 mx-auto text-zinc-300 mb-4" />
          <div className="overline mb-2">{t("payments.emptyTitle")}</div>
        </div>
      ) : (
        <div className="border border-zinc-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 border-b border-zinc-200">
              <tr className="text-left">
                <Th>{t("common.date")}</Th><Th>{t("rentals.customer")}</Th><Th>{t("payments.contractCol")}</Th><Th>{t("payments.method")}</Th><Th>{t("common.amount")}</Th><Th>{t("common.status")}</Th><Th></Th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50">
                  <Td className="font-mono text-xs">{p.paid_at ? format(parseISO(p.paid_at), "dd MMM yyyy", { locale: dateLocale }) : "—"}</Td>
                  <Td>{p.customer_name || "—"}</Td>
                  <Td className="text-xs font-mono">{p.rental_id.slice(0, 8)}</Td>
                  <Td className="text-xs">{t(`payments.methods.${p.method}`)}</Td>
                  <Td className="font-mono font-semibold">{fmtCurrency(p.amount)}</Td>
                  <Td>
                    <span className={`text-[10px] font-mono uppercase tracking-wider px-2 py-1 border ${p.status === "pago" ? "bg-green-100 text-green-800 border-green-200" : "bg-amber-100 text-amber-800 border-amber-200"}`}>{t(`payments.status.${p.status}`)}</span>
                  </Td>
                  <Td>
                    <div className="flex gap-1 justify-end">
                      {p.status === "pago" && (
                        <button
                          onClick={() => downloadReceipt(p.id)}
                          className="p-1.5 border border-zinc-200 hover:border-[#F5A623] hover:text-[#F5A623]"
                          title={t("payments.downloadReceipt")}
                          data-testid={`download-receipt-${p.id}`}
                        >
                          <FileDown className="w-3 h-3" />
                        </button>
                      )}
                      <button onClick={() => remove(p.id)} className="p-1.5 border border-zinc-200 hover:border-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const Th = ({ children }) => <th className="px-4 py-3 overline">{children}</th>;
const Td = ({ children, className = "" }) => <td className={`px-4 py-3 ${className}`}>{children}</td>;
const Field = ({ label, children }) => (
  <div className="space-y-1.5"><label className="overline">{label}</label>{children}</div>
);
